// =======================================================
// Entwurf der Meldung über „Feedback"
//
// Der Entwurf lebt hier und nicht im Dialog: Wer ein Ticket anfängt und noch
// etwas anklicken will, minimiert den Dialog — Text, Anhänge und Fotos bleiben
// beim Seitenwechsel erhalten. Der Store hängt am Knopf in der Seitenleiste,
// also oberhalb der Seiten. Der Text wird zusätzlich im Tab-Speicher
// (sessionStorage) gesichert, damit ein Neuladen ihn nicht kostet; Dateien
// gehen dabei nicht mit. Beim Abmelden oder Benutzerwechsel wird beides
// gelöscht (geteilte Geräte).
// =======================================================
import { create } from 'zustand';
import { useAuthStore } from './authStore';
import {
  MAX_ANHAENGE, dateienPruefen, screenshotAufnehmen, screenshotDateiname, seitenKontextLesen,
  type SeitenKontext, type WeitereSeite,
} from '../lib/meldung';

export type FotoStand = 'laeuft' | 'fertig' | 'fehlt';

const SPEICHER_KEY = 'cc-melden-entwurf';
const HINWEIS_MS = 4000;

interface EntwurfDaten {
  /** Gibt es einen Entwurf (offen oder minimiert)? */
  entwurf: boolean;
  /** Konto, dem der Entwurf gehört. */
  besitzer: string | null;
  kontext: SeitenKontext | null;
  beschreibung: string;
  screenshot: File | null;
  fotoStand: FotoStand;
  fotoMit: boolean;
  dateien: File[];
  weitereSeiten: WeitereSeite[];
}

interface EntwurfState extends EntwurfDaten {
  /** Dialog sichtbar? Sonst (bei vorhandenem Entwurf) die Leiste. */
  offen: boolean;
  /** Nimmt die Leiste gerade ein Foto auf? */
  fotoLaeuft: boolean;
  /** Kurze Rückmeldung der Leiste („Screenshot hinzugefügt (2)"). */
  hinweis: string;

  oeffnen: () => void;
  /** Dialog zu: ein leerer Entwurf verschwindet, ein angefangener bleibt. */
  schliessen: () => void;
  /** Dialog zu, Entwurf bleibt in jedem Fall (etwa um eine andere Seite zu fotografieren). */
  minimieren: () => void;
  verwerfen: () => void;
  /** Nach erfolgreichem Senden: Entwurf leeren, Sichtbarkeit unverändert lassen. */
  gesendet: () => void;
  ersteFotoStarten: () => Promise<void>;
  setBeschreibung: (text: string) => void;
  fotoUmschalten: () => void;
  /** Gibt einen Fehlertext zurück, falls etwas abgelehnt wurde. */
  hinzufuegen: (neu: File[]) => string;
  dateiEntfernen: (index: number) => void;
  weitereSeiteAufnehmen: () => Promise<void>;
  wiederherstellen: (userId: string) => void;
}

const LEER: EntwurfDaten = {
  entwurf: false,
  besitzer: null,
  kontext: null,
  beschreibung: '',
  screenshot: null,
  fotoStand: 'laeuft',
  fotoMit: true,
  dateien: [],
  weitereSeiten: [],
};

/** Belegte Bildplätze: das Foto der Seite (auch solange es entsteht) plus alle Anhänge. */
export function belegtePlaetze(s: Pick<EntwurfDaten, 'screenshot' | 'fotoStand' | 'fotoMit' | 'dateien'>): number {
  const foto = s.fotoMit && (s.screenshot || s.fotoStand === 'laeuft') ? 1 : 0;
  return foto + s.dateien.length;
}

/** Noch nichts getippt und nichts angehängt (das automatische Foto zählt nicht). */
export function entwurfIstLeer(s: Pick<EntwurfDaten, 'beschreibung' | 'dateien' | 'weitereSeiten'>): boolean {
  return s.beschreibung.trim() === '' && s.dateien.length === 0 && s.weitereSeiten.length === 0;
}

/** Anzahl der Bilder, die mit dem Ticket gehen würden. */
export function anzahlBilder(s: Pick<EntwurfDaten, 'screenshot' | 'fotoMit' | 'dateien'>): number {
  return (s.fotoMit && s.screenshot ? 1 : 0) + s.dateien.length;
}

// ------------------------------------------------------- Tab-Speicher (Text)
function textSichern(besitzer: string | null, text: string) {
  try {
    if (besitzer && text.trim()) {
      sessionStorage.setItem(SPEICHER_KEY, JSON.stringify({ userId: besitzer, text }));
    } else {
      sessionStorage.removeItem(SPEICHER_KEY);
    }
  } catch { /* Speicher gesperrt: dann eben ohne Sicherung */ }
}

function textLesen(userId: string): string | null {
  try {
    const roh = sessionStorage.getItem(SPEICHER_KEY);
    if (!roh) return null;
    const d = JSON.parse(roh) as { userId?: string; text?: string };
    return d.userId === userId && typeof d.text === 'string' && d.text.trim() ? d.text : null;
  } catch {
    return null;
  }
}

let hinweisTimer: ReturnType<typeof setTimeout> | undefined;
// Zählt Entwürfe hoch: ein Foto, das erst nach dem Verwerfen fertig wird,
// darf nicht in einem neuen Entwurf landen.
let lauf = 0;
// Für welchen Entwurf das erste Foto schon läuft — der Dialog darf beim
// erneuten Öffnen kein zweites anstoßen.
let fotoGestartetFuer = -1;

export const useMeldungEntwurf = create<EntwurfState>()((set, get) => {
  const hinweisZeigen = (text: string) => {
    clearTimeout(hinweisTimer);
    set({ hinweis: text });
    hinweisTimer = setTimeout(() => set({ hinweis: '' }), HINWEIS_MS);
  };

  const zuruecksetzen = (offen: boolean) => {
    lauf++;
    clearTimeout(hinweisTimer);
    textSichern(null, '');
    set({ ...LEER, offen, fotoLaeuft: false, hinweis: '' });
  };

  return {
    ...LEER,
    offen: false,
    fotoLaeuft: false,
    hinweis: '',

    oeffnen: () => {
      if (!get().entwurf) {
        lauf++;
        set({
          ...LEER,
          entwurf: true,
          besitzer: useAuthStore.getState().user?.id ?? null,
          // Wo die Person stand, als sie geklickt hat.
          kontext: seitenKontextLesen(),
        });
      }
      set({ offen: true, hinweis: '' });
    },

    schliessen: () => {
      if (entwurfIstLeer(get())) zuruecksetzen(false);
      else set({ offen: false });
    },

    minimieren: () => set({ offen: false }),

    verwerfen: () => zuruecksetzen(false),

    gesendet: () => zuruecksetzen(get().offen),

    ersteFotoStarten: async () => {
      const mein = lauf;
      if (fotoGestartetFuer === mein) return;
      fotoGestartetFuer = mein;
      const f = await screenshotAufnehmen();
      if (mein !== lauf || !get().entwurf) return;
      set({ screenshot: f, fotoStand: f ? 'fertig' : 'fehlt' });
    },

    setBeschreibung: (text) => {
      set({ beschreibung: text });
      textSichern(get().besitzer, text);
    },

    fotoUmschalten: () => set((s) => ({ fotoMit: !s.fotoMit })),

    hinzufuegen: (neu) => {
      if (neu.length === 0) return '';
      const s = get();
      const platz = MAX_ANHAENGE - (belegtePlaetze(s) - s.dateien.length);
      const { dateien, fehler } = dateienPruefen(s.dateien, neu, platz);
      set({ dateien });
      return fehler;
    },

    dateiEntfernen: (index) => {
      const s = get();
      const weg = s.dateien[index];
      if (!weg) return;
      set({
        dateien: s.dateien.filter((_, j) => j !== index),
        weitereSeiten: s.weitereSeiten.filter((w) => w.dateiName !== weg.name),
      });
    },

    weitereSeiteAufnehmen: async () => {
      const s = get();
      if (!s.entwurf || s.fotoLaeuft) return;
      if (belegtePlaetze(s) >= MAX_ANHAENGE) {
        hinweisZeigen(
          `Es sind schon ${MAX_ANHAENGE} Bilder dabei — mehr geht nicht. Entferne im Entwurf ein Bild, dann ist wieder Platz.`,
        );
        return;
      }
      // Seite und Zeit jetzt festhalten, bevor das Foto (kurz) dauert.
      const ort = seitenKontextLesen();
      const zeit = new Date();
      let name = screenshotDateiname(ort.seite, zeit);
      if (s.dateien.some((d) => d.name === name)) name = name.replace(/\.png$/, `-${s.dateien.length + 1}.png`);

      const mein = lauf;
      set({ fotoLaeuft: true });
      const datei = await screenshotAufnehmen(name);
      if (mein !== lauf) return;
      set({ fotoLaeuft: false });
      if (!datei) {
        hinweisZeigen('Das Foto dieser Seite hat nicht geklappt. Du kannst es noch einmal versuchen.');
        return;
      }
      const jetzt = get();
      // Während des Fotos kann der Platz anderweitig belegt worden sein.
      if (belegtePlaetze(jetzt) >= MAX_ANHAENGE) {
        hinweisZeigen(`Es sind schon ${MAX_ANHAENGE} Bilder dabei — mehr geht nicht.`);
        return;
      }
      const dateien = [...jetzt.dateien, datei];
      set({
        dateien,
        weitereSeiten: [...jetzt.weitereSeiten, { seite: ort.seite, link: ort.link, zeitpunkt: zeit, dateiName: datei.name }],
      });
      hinweisZeigen(`Screenshot hinzugefügt (${anzahlBilder(get())})`);
    },

    wiederherstellen: (userId) => {
      if (get().entwurf) return;
      const text = textLesen(userId);
      if (!text) return;
      lauf++;
      // Ohne das ursprüngliche Foto: die Seite von damals gibt es nicht mehr.
      set({
        ...LEER,
        entwurf: true,
        besitzer: userId,
        kontext: seitenKontextLesen(),
        beschreibung: text,
        fotoStand: 'fehlt',
        offen: false,
      });
    },
  };
});

// Anderes Konto oder Abmeldung: Entwurf samt gesichertem Text weg.
useAuthStore.subscribe((s) => {
  const aktuell = s.user?.id ?? null;
  const { besitzer, entwurf, verwerfen } = useMeldungEntwurf.getState();
  if (entwurf && besitzer !== aktuell) verwerfen();
});
